import React from "react";

import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { rest, t } from "@auto/services";
import { getServiceUri } from "@auto/metadata";

import styles from "./TabletReportsFacets.module.styl";

const STATE_OPTIONS = [
  {
    value: "OK",
    label: "reporting.tabletOperational"
  },
  {
    value: "STALE",
    label: "reporting.tabletStale"
  },
  {
    value: "OUTDATED",
    label: "reporting.tabletOutdated"
  },
  {
    value: "APP_MISSING",
    label: "reporting.tabletAppMissing"
  }
];

export const DEFAULT_TABLET_REPORT_FILTERS = Object.freeze({
  states: Object.freeze([]),
  includeUnassigned: false
});

const normalizeArray = (value) =>
  Array.isArray(value)
    ? value
        .map((item) =>
          String(item || "")
            .trim()
            .toUpperCase()
        )
        .filter(Boolean)
    : [];

export const normalizeTabletReportFilters = (value) => {
  const input =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  return {
    states: normalizeArray(input.states),
    includeUnassigned:
      input.includeUnassigned === true
  };
};

export const isDefaultTabletReportFilters = (value) => {
  const filters =
    normalizeTabletReportFilters(value);

  return (
    filters.states.length === 0 &&
    !filters.includeUnassigned
  );
};

export const getTabletReportActiveFilterCount = (value) => {
  const filters =
    normalizeTabletReportFilters(value);

  return (
    filters.states.length +
    (filters.includeUnassigned ? 1 : 0)
  );
};

export const buildTabletReportPayload = ({
  search,
  filters,
  limit,
  offset,
  sort
}) => ({
  search: String(search || "").trim(),
  filters: normalizeTabletReportFilters(filters),
  limit,
  offset,
  ...(Array.isArray(sort) && sort.length
    ? { sort }
    : {})
});

export const loadTabletReportFacetCounts = async ({
  search,
  filters
}) => {
  return await rest.request(
    getServiceUri() +
      "reporting/tablet-reports/facets",
    "POST",
    buildTabletReportPayload({
      search,
      filters,
      limit: null,
      offset: null
    })
  );
};

const toggleArrayValue = (values, value) => {
  const current = normalizeArray(values);

  return current.includes(value)
    ? current.filter((item) => item !== value)
    : [...current, value];
};

const countFor = (
  counts,
  path,
  fallback = 0
) => {
  let current = counts || {};

  for (const key of path) {
    if (
      !current ||
      typeof current !== "object"
    ) {
      return fallback;
    }

    current = current[key];
  }

  const number = Number(current);

  return Number.isFinite(number)
    ? number
    : fallback;
};

const FacetSection = ({
  title,
  children
}) => (
  <section className={styles.section}>
    <div className={styles.sectionTitle}>
      {title}
    </div>

    <div className={styles.sectionBody}>
      {children}
    </div>
  </section>
);

const CheckboxOption = ({
  label,
  selected,
  count,
  onClick
}) => (
  <button
    type="button"
    className={
      selected
        ? `${styles.option} ${styles.optionSelected}`
        : styles.option
    }
    onClick={onClick}
  >
    <span className={styles.checkBox}>
      {selected ? "✓" : ""}
    </span>

    <span className={styles.optionLabel}>
      {label}
    </span>

    <span className={styles.count}>
      {count}
    </span>
  </button>
);

const Chip = ({
  label,
  onRemove
}) => (
  <button
    type="button"
    className={styles.chip}
    onClick={onRemove}
  >
    <span>{label}</span>
    <span className={styles.chipClose}>×</span>
  </button>
);

export const TabletReportsFacetButton = ({
  controller
}) => {
  const activeCount =
    controller?.activeCount || 0;

  return (
    <button
      type="button"
      className={
        activeCount
          ? `${styles.filterButton} ${styles.filterButtonActive}`
          : styles.filterButton
      }
      onClick={controller.toggleDrawer}
    >
      <span className={styles.filterButtonIcon}>
        <TuneIcon fontSize="inherit" />
      </span>

      <span className={styles.filterButtonLabel}>
        {t("common.filters")}
      </span>

      {activeCount ? (
        <span className={styles.filterButtonBadge}>
          {activeCount}
        </span>
      ) : null}
    </button>
  );
};

export const TabletReportsFacetChips = ({
  controller
}) => {
  const filters =
    normalizeTabletReportFilters(
      controller?.value
    );

  if (
    isDefaultTabletReportFilters(filters)
  ) {
    return null;
  }

  return (
    <div className={styles.chipsBar}>
      {filters.states.map((value) => {
        const option =
          STATE_OPTIONS.find(
            (item) =>
              item.value === value
          );

        return (
          <Chip
            key={value}
            label={
              option
                ? t(option.label)
                : value
            }
            onRemove={() =>
              controller.patchValue({
                states:
                  filters.states.filter(
                    (item) =>
                      item !== value
                  )
              })
            }
          />
        );
      })}

      {filters.includeUnassigned ? (
        <Chip
          label={t(
            "reporting.tabletIncludeUnassigned"
          )}
          onRemove={() =>
            controller.patchValue({
              includeUnassigned: false
            })
          }
        />
      ) : null}

      <button
        type="button"
        className={styles.clearAll}
        onClick={controller.reset}
      >
        {t("common.clearAll")}
      </button>
    </div>
  );
};

const TabletReportsFacets = ({
  controller
}) => {
  const filters =
    normalizeTabletReportFilters(
      controller?.value
    );

  const counts =
    controller?.counts || {};

  return (
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div>
          <div className={styles.drawerTitle}>
            {t("common.filters")}
          </div>

          <div className={styles.drawerSubtitle}>
            {controller.countsLoading
              ? t("common.updatingCounts")
              : t("filter.refineTablets")}
          </div>
        </div>

        <button
          type="button"
          className={styles.closeButton}
          onClick={controller.closeDrawer}
          aria-label={t(
            "common.closeFilters"
          )}
        >
          <CloseIcon fontSize="inherit" />
        </button>
      </div>

      <div className={styles.drawerBody}>
        <FacetSection
          title={t(
            "reporting.tabletCondition"
          )}
        >
          {STATE_OPTIONS.map(
            (option) => (
              <CheckboxOption
                key={option.value}
                label={t(option.label)}
                selected={
                  filters.states.includes(
                    option.value
                  )
                }
                count={countFor(
                  counts,
                  [
                    "state",
                    option.value
                  ]
                )}
                onClick={() =>
                  controller.patchValue({
                    states:
                      toggleArrayValue(
                        filters.states,
                        option.value
                      )
                  })
                }
              />
            )
          )}
        </FacetSection>

        <FacetSection
          title={t(
            "reporting.tabletAssignment"
          )}
        >
          <CheckboxOption
            label={t(
              "reporting.tabletIncludeUnassigned"
            )}
            selected={
              filters.includeUnassigned
            }
            count={countFor(
              counts,
              [
                "assignment",
                "UNASSIGNED"
              ]
            )}
            onClick={() =>
              controller.patchValue({
                includeUnassigned:
                  !filters.includeUnassigned
              })
            }
          />
        </FacetSection>
      </div>

      <div className={styles.drawerFooter}>
        <button
          type="button"
          className={styles.secondaryAction}
          onClick={controller.reset}
          disabled={
            controller.activeCount === 0
          }
        >
          <RestartAltIcon fontSize="inherit" />
          <span>
            {t("common.resetFilters")}
          </span>
        </button>

        <button
          type="button"
          className={styles.primaryAction}
          onClick={controller.closeDrawer}
        >
          {t("common.done")}
        </button>
      </div>
    </div>
  );
};

export const TABLET_REPORTS_FACETS = {
  storageKey:
    "haiti.tablet-reports.facets.v2",

  overlayLabel:
    t("filter.tabletFilters"),

  defaultValue:
    DEFAULT_TABLET_REPORT_FILTERS,

  normalizeValue:
    normalizeTabletReportFilters,

  isDefaultValue:
    isDefaultTabletReportFilters,

  getActiveCount:
    getTabletReportActiveFilterCount,

  loadCounts:
    loadTabletReportFacetCounts,

  renderButton: (controller) => (
    <TabletReportsFacetButton
      controller={controller}
    />
  ),

  renderDrawer: (controller) => (
    <TabletReportsFacets
      controller={controller}
    />
  ),

  renderChips: (controller) => (
    <TabletReportsFacetChips
      controller={controller}
    />
  )
};

export default TabletReportsFacets;
